import { supabase } from '../lib/supabase';

/**
 * Progress Service
 * Handles all exercise progress tracking with Supabase backend
 * Syncs with localStorage as fallback
 */

class ProgressService {
  /**
   * Load exercise progress for current user
   * @param {string} exerciseId - e.g., 'thinking-in-data'
   * @returns {Promise<object>} Progress data
   */
  async loadExerciseProgress(exerciseId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return this._loadFromLocalStorage(exerciseId);
      }

      // Try to load from database
      const { data, error } = await supabase
        .from('exercise_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('exercise_id', exerciseId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
        console.error('Error loading progress:', error);
        return this._loadFromLocalStorage(exerciseId);
      }

      if (!data) {
        // No progress yet, check localStorage for migration
        const localProgress = this._loadFromLocalStorage(exerciseId);
        if (localProgress.completedCases.length > 0 || Object.keys(localProgress.completedChallenges).length > 0) {
          // Migrate localStorage to database
          await this._migrateLocalStorageToDatabase(exerciseId, localProgress);
          return localProgress;
        }
        return { completedCases: [], completedChallenges: {} };
      }

      // Load case and challenge progress
      const { data: caseProgress } = await supabase
        .from('case_progress')
        .select('case_id, completed_at')
        .eq('user_id', user.id)
        .eq('exercise_id', exerciseId);

      const { data: challengeProgress } = await supabase
        .from('challenge_progress')
        .select('case_id, challenge_id, completed')
        .eq('user_id', user.id)
        .eq('exercise_id', exerciseId)
        .eq('completed', true);

      // Transform to match localStorage format
      const completedCases = (caseProgress || [])
        .filter(c => c.completed_at)
        .map(c => c.case_id);  // return raw caseId string (semantic slugs like 'data-viz-intro')

      const completedChallenges = (challengeProgress || []).reduce((acc, ch) => {
        const caseKey = `case_${ch.case_id.replace('case-', '')}`;
        if (!acc[caseKey]) acc[caseKey] = [];
        // Deduplicate: only push if not already present
        if (!acc[caseKey].includes(ch.challenge_id)) acc[caseKey].push(ch.challenge_id);
        return acc;
      }, {})

      return { completedCases, completedChallenges };
    } catch (error) {
      console.error('Error loading exercise progress:', error);
      return this._loadFromLocalStorage(exerciseId);
    }
  }

  /**
   * Save exercise progress
   * @param {string} exerciseId 
   * @param {object} progress - { completedCases: [], completedChallenges: {} }
   */
  async saveExerciseProgress(exerciseId, progress) {
    // Always save to localStorage as backup
    this._saveToLocalStorage(exerciseId, progress);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Upsert exercise progress
      await supabase
        .from('exercise_progress')
        .upsert({
          user_id: user.id,
          exercise_id: exerciseId,
          completed_cases: progress.completedCases.length,
          total_cases: progress.totalCases || 0,
          last_accessed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,exercise_id'
        });
    } catch (error) {
      console.error('Error saving exercise progress:', error);
    }
  }

  /**
   * Mark a case as completed
   * @param {string} exerciseId 
   * @param {string} caseId - e.g., 'case-1' or 1
   */
  async completeCase(exerciseId, caseId) {
    const normalizedCaseId = typeof caseId === 'number' ? `case-${caseId}` : caseId;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('case_progress')
        .upsert({
          user_id: user.id,
          exercise_id: exerciseId,
          case_id: normalizedCaseId,
          completed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,exercise_id,case_id'
        });
    } catch (error) {
      console.error('Error completing case:', error);
    }
  }

  /**
   * Record that a user opened/started a challenge.
   * Uses ignoreDuplicates so a pre-existing row (and its started_at) is never overwritten.
   * @param {string} exerciseId
   * @param {string|number} caseId
   * @param {string} challengeId
   */
  async startChallenge(exerciseId, caseId, challengeId) {
    const normalizedCaseId = typeof caseId === 'number' ? `case-${caseId}` : caseId;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase
        .from('challenge_progress')
        .upsert({
          user_id: user.id,
          exercise_id: exerciseId,
          case_id: normalizedCaseId,
          challenge_id: challengeId,
          completed: false,
        }, {
          onConflict: 'user_id,exercise_id,case_id,challenge_id',
          ignoreDuplicates: true  // never overwrite an existing row - preserves created_at as start time
        });
    } catch (error) {
      console.error('Error starting challenge:', error);
    }
  }

  /**
   * Mark a challenge as completed
   * @param {string} exerciseId 
   * @param {string} caseId 
   * @param {string} challengeId 
   * @param {object} metadata - { solution, hintsUsed, solutionViewed, timeSpent }
   */
  async completeChallenge(exerciseId, caseId, challengeId, metadata = {}) {
    const normalizedCaseId = typeof caseId === 'number' ? `case-${caseId}` : caseId;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Read existing row so we can preserve first-occurrence timestamps and increment attempts
      const { data: existing } = await supabase
        .from('challenge_progress')
        .select('completed_at, attempts')
        .eq('user_id', user.id)
        .eq('exercise_id', exerciseId)
        .eq('case_id', normalizedCaseId)
        .eq('challenge_id', challengeId)
        .single();

      const now = new Date().toISOString();

      await supabase
        .from('challenge_progress')
        .upsert({
          user_id: user.id,
          exercise_id: exerciseId,
          case_id: normalizedCaseId,
          challenge_id: challengeId,
          completed: true,
          // Preserve original - only set on first completion
          completed_at: existing?.completed_at || now,
          // Always increment
          attempts: (existing?.attempts || 0) + 1,
          user_solution: metadata.solution || null,
          hints_used: metadata.hintsUsed || 0,
          solution_viewed: metadata.solutionViewed || false,
          time_spent_seconds: metadata.timeSpent || 0
        }, {
          onConflict: 'user_id,exercise_id,case_id,challenge_id'
        });
    } catch (error) {
      console.error('Error completing challenge:', error);
    }
  }

  /**
   * Get user's overall stats
   */
  async getUserStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_exercise_stats')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user stats:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting user stats:', error);
      return null;
    }
  }

  /**
   * Get leaderboard data
   * @param {string} exerciseId - Optional: filter by exercise
   * @param {number} limit - Number of top users to return
   */
  async getLeaderboard(exerciseId = null, limit = 10) {
    try {
      let query = supabase
        .from('challenge_progress')
        .select(`
          user_id,
          exercise_id,
          count:challenge_id.count(),
          total_time:time_spent_seconds.sum()
        `)
        .eq('completed', true);

      if (exerciseId) {
        query = query.eq('exercise_id', exerciseId);
      }

      const { data, error } = await query
        .order('count', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error loading leaderboard:', error);
        return [];
      }

      return data;
    } catch (error) {
      console.error('Error getting leaderboard:', error);
      return [];
    }
  }

  /**
   * Synchronous read from localStorage cache - no DB call.
   * Use for instant initial state before the async DB load completes.
   */
  getCachedProgress(exerciseId) {
    return this._loadFromLocalStorage(exerciseId);
  }

  // Private helper methods
  _loadFromLocalStorage(exerciseId) {
    const key = `${exerciseId}Progress`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          completedCases: parsed.completed || [],
          completedChallenges: parsed.challenges || {}
        };
      } catch (e) {
        console.error('Error parsing localStorage:', e);
      }
    }
    return { completedCases: [], completedChallenges: {} };
  }

  _saveToLocalStorage(exerciseId, progress) {
    const key = `${exerciseId}Progress`;
    // Merge completedChallenges so that saving from one module doesn't
    // wipe out saved data from other modules stored under the same exerciseId.
    const existing = this._loadFromLocalStorage(exerciseId);
    const mergedChallenges = { ...existing.completedChallenges, ...progress.completedChallenges };
    localStorage.setItem(key, JSON.stringify({
      completed: progress.completedCases,
      challenges: mergedChallenges
    }));
  }

  async _migrateLocalStorageToDatabase(exerciseId, localProgress) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Migrate completed cases
      for (const caseId of localProgress.completedCases) {
        await this.completeCase(exerciseId, caseId);
      }

      // Migrate completed challenges
      for (const [caseKey, challenges] of Object.entries(localProgress.completedChallenges)) {
        const caseId = caseKey.replace('case_', '');
        for (const challengeId of challenges) {
          await this.completeChallenge(exerciseId, caseId, challengeId);
        }
      }

      console.log('Successfully migrated localStorage to database');
    } catch (error) {
      console.error('Error migrating to database:', error);
    }
  }
}

// Export singleton instance
export const progressService = new ProgressService();
